import React, { useMemo, useState } from "react";
import {
  ShoppingCart,
  Sparkles,
  Clock3,
  Truck,
  CheckCircle2,
  XCircle,
  IndianRupee,
  Package,
  UserX,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

const PIPELINE_STATUSES = new Set([
  "Pending",
  "Processing",
  "Packed",
  "Shipped",
  "Out for delivery",
]);

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function isArchived(order) {
  return order.archive === true || order.archive === "true";
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "Today"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-[280px]">
      <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-line bg-surface-2 p-1">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "text-ink-muted hover:bg-white/10 hover:text-ink"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="mt-2 rounded-xl border border-dashed border-line-strong bg-surface-2 p-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="rounded-lg border border-line px-3 py-1 text-xs font-semibold text-ink-secondary hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon, active, onClick }) => {
  const toneClass =
    tone === "success"
      ? "text-lime-300"
      : tone === "warn"
        ? "text-amber-300"
        : tone === "danger"
          ? "text-rose-300"
          : "text-ink";

  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg border px-3 py-2 min-w-[118px] text-left transition-all ${
        active
          ? "border-brand-lime bg-white/10 ring-2 ring-lime-500/30 shadow-sm"
          : "border-line bg-white/5 hover:border-brand-lime/40 hover:bg-white/10"
      } ${onClick ? "cursor-pointer" : ""}`}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">{label}</p>
        {Icon ? <Icon size={14} className="text-ink-muted shrink-0" /> : null}
      </div>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-ink-muted mt-0.5">{hint}</p> : null}
    </Tag>
  );
};

const STATUS_BAR = [
  { key: "Pending", color: "#f59e0b", tab: "pending" },
  { key: "Processing", color: "#3b82f6", tab: "processing" },
  { key: "Packed", color: "#10b981", tab: "packed" },
  { key: "Shipped", color: "#06b6d4", tab: "shipped" },
  { key: "Out for delivery", color: "#a855f7", tab: "out for delivery" },
  { key: "Delivered", color: "#22c55e", tab: "delivered" },
  { key: "Cancelled", color: "#ef4444", tab: "cancelled" },
];

export default function OrderPageSummary({ orders = [], activeTab, onTabChange }) {
  const [filter, setFilter] = useState("All Time");

  const summary = useMemo(() => {
    const periodOrders = (Array.isArray(orders) ? orders : []).filter(
      (order) => !isArchived(order) && isInFilter(order.createdAt || order.updatedAt, filter)
    );

    const countByStatus = (status) =>
      periodOrders.filter((order) => order.orderStatus === status).length;

    const deliveredOrders = periodOrders.filter((order) => order.orderStatus === "Delivered");
    const revenue = deliveredOrders.reduce(
      (sum, order) => sum + (Number(order.amountafterdiscount) || 0),
      0
    );
    const pipelineOrders = periodOrders.filter(
      (order) => PIPELINE_STATUSES.has(order.orderStatus)
    );
    const pipelineValue = pipelineOrders.reduce(
      (sum, order) => sum + (Number(order.amountafterdiscount) || 0),
      0
    );
    const unassigned = periodOrders.filter(
      (order) => !order.staff_id && !order.staff_name
    ).length;
    const paymentPending = periodOrders.filter(
      (order) => String(order.status || "").toLowerCase() === "pending"
    ).length;

    const statusCounts = STATUS_BAR.map((item) => ({
      ...item,
      count: countByStatus(item.key),
    }));
    const statusTotal = statusCounts.reduce((sum, item) => sum + item.count, 0) || 1;

    return {
      filterLabel: getFilterLabel(filter),
      total: periodOrders.length,
      newCount: periodOrders.filter((order) => order.mark === "New").length,
      pending: countByStatus("Pending"),
      processing: countByStatus("Processing"),
      packed: countByStatus("Packed"),
      shipped: countByStatus("Shipped"),
      outForDelivery: countByStatus("Out for delivery"),
      delivered: deliveredOrders.length,
      cancelled: countByStatus("Cancelled"),
      pipeline: pipelineOrders.length,
      revenue,
      pipelineValue,
      avgOrderValue: deliveredOrders.length ? Math.round(revenue / deliveredOrders.length) : 0,
      unassigned,
      paymentPending,
      statusCounts,
      statusTotal,
    };
  }, [orders, filter]);

  const switchTab = (tab) => {
    if (onTabChange) onTabChange(tab);
  };

  return (
    <section className="order-page-summary mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-ink">Order Summary</h2>
          <p className="text-xs sm:text-sm text-ink-muted">
            Fulfillment pulse, revenue, and pipeline health — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-brand-lime/20 bg-gradient-to-br from-brand-lime/10 to-emerald-500/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-lime-300 shadow-sm">
              <ShoppingCart size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Volume</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--orders pb-1">
            <MetricCard
              label="Total Orders"
              value={summary.total}
              hint="In selected period"
              icon={ShoppingCart}
              active={activeTab === "all"}
              onClick={() => switchTab("all")}
            />
            <MetricCard
              label="New"
              value={summary.newCount}
              hint="Unread orders"
              icon={Sparkles}
              tone={summary.newCount > 0 ? "warn" : "default"}
              active={activeTab === "new"}
              onClick={() => switchTab("new")}
            />
            <MetricCard
              label="In Pipeline"
              value={summary.pipeline}
              hint="Pending to delivery"
              icon={Truck}
              tone="warn"
            />
            <MetricCard
              label="Unassigned"
              value={summary.unassigned}
              hint="No staff assigned"
              icon={UserX}
              tone={summary.unassigned > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Payment Pending"
              value={summary.paymentPending}
              hint="Awaiting payment"
              icon={Clock3}
              tone={summary.paymentPending > 0 ? "danger" : "default"}
            />
          </div>
        </div>

        <div className="rounded-xl border border-brand-cyan/20 bg-gradient-to-br from-brand-cyan/10 to-brand-indigo/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-brand-cyan shadow-sm">
              <IndianRupee size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Revenue</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--orders pb-1">
            <MetricCard
              label="Delivered Revenue"
              value={formatCurrency(summary.revenue)}
              hint="Delivered order value"
              icon={IndianRupee}
              tone="success"
              active={activeTab === "delivered"}
              onClick={() => switchTab("delivered")}
            />
            <MetricCard
              label="Pipeline Value"
              value={formatCurrency(summary.pipelineValue)}
              hint="Open order value"
              icon={Package}
            />
            <MetricCard
              label="Avg Order Value"
              value={formatCurrency(summary.avgOrderValue)}
              hint="Per delivered order"
            />
            <MetricCard
              label="Delivered"
              value={summary.delivered}
              icon={CheckCircle2}
              tone="success"
              active={activeTab === "delivered"}
              onClick={() => switchTab("delivered")}
            />
            <MetricCard
              label="Cancelled"
              value={summary.cancelled}
              icon={XCircle}
              tone={summary.cancelled > 0 ? "danger" : "default"}
              active={activeTab === "cancelled"}
              onClick={() => switchTab("cancelled")}
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-surface p-3 sm:p-4 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
          Status breakdown
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-3">
          {summary.statusCounts.map((item) =>
            item.count > 0 ? (
              <button
                key={item.key}
                type="button"
                title={`${item.key}: ${item.count}`}
                onClick={() => switchTab(item.tab)}
                className="h-full transition-opacity hover:opacity-80"
                style={{
                  width: `${(item.count / summary.statusTotal) * 100}%`,
                  backgroundColor: item.color,
                  minWidth: item.count > 0 ? "4px" : 0,
                }}
                aria-label={`${item.key} ${item.count}`}
              />
            ) : null
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-muted">
          {summary.statusCounts.map((item) => (
            <button
              key={`${item.key}-legend`}
              type="button"
              onClick={() => switchTab(item.tab)}
              className={`inline-flex items-center gap-1 hover:text-ink ${
                activeTab === item.tab ? "font-bold text-ink" : ""
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.key} ({item.count})
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-ink-secondary">
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <Sparkles size={14} className="text-amber-400 mt-0.5 shrink-0" />
          <span>
            {summary.newCount > 0
              ? `${summary.newCount} new order(s) need a first review. Open the New tab to process them.`
              : "No unread new orders in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <Truck size={14} className="text-brand-cyan mt-0.5 shrink-0" />
          <span>
            {summary.pipeline > 0
              ? `${summary.pipeline} order(s) are still in the fulfillment pipeline worth ${formatCurrency(summary.pipelineValue)}.`
              : "No open pipeline orders in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <CheckCircle2 size={14} className="text-lime-400 mt-0.5 shrink-0" />
          <span>
            {summary.delivered > 0
              ? `${summary.delivered} delivered order(s) generated ${formatCurrency(summary.revenue)} in this period.`
              : "No delivered orders in this period yet."}
          </span>
        </div>
      </div>
    </section>
  );
}
